import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getAdminCoursePreview, GetAdminCoursePreviewOutputType, getCourseBuilderModules, GetCourseBuilderModulesOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ChevronRight, ChevronLeft, CheckCircle, Play, Clock,
  BookOpen, AlertTriangle, ShieldAlert, FileText, Eye, Award, Video
} from 'lucide-react';
import V2PreviewPlayer from '@/components/training-admin/V2PreviewPlayer';

type PreviewData = GetAdminCoursePreviewOutputType;
type Module = PreviewData['modules'][0];

function MissingBanner({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="bg-muted border border-border rounded-lg p-3 space-y-1">
      {items.map((item, i) => (
        <p key={i} className="text-xs text-muted-foreground flex items-center gap-2">
          <AlertTriangle className="w-3 h-3 text-muted-foreground shrink-0" />
          {item}
        </p>
      ))}
    </div>
  );
}

function SectionContent({ section }: { section: { title: string; content: string } }) {
  return (
    <div className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">
      {section.content || 'Content coming soon…'}
    </div>
  );
}

function ModulePreview({ mod, onClose }: { mod: Module; onClose: () => void }) {
  const [currentSection, setCurrentSection] = useState(0);
  const sections = mod.sections;
  const hasSections = sections.length > 0;

  let practicalItems: any[] = [];
  try { practicalItems = JSON.parse(mod.practicalAssessmentItems || '[]'); } catch {}

  return (
    <div className="max-w-2xl mx-auto pb-24">
      <div className="px-4 py-3 border-b border-border bg-card sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <button onClick={onClose} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to Course
          </button>
          <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded flex items-center gap-1">
            <Eye className="w-3 h-3" /> Preview Mode
          </span>
        </div>
        {hasSections && (
          <div className="flex gap-1 mt-2">
            {sections.map((_, i) => (
              <button key={i} onClick={() => setCurrentSection(i)}
                className={`flex-1 h-2 rounded-full transition-colors ${i === currentSection ? 'bg-primary' : 'bg-muted'}`} />
            ))}
          </div>
        )}
      </div>

      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">{mod.displayOrder}</span>
        </div>
        <h1 className="text-lg font-bold text-foreground">{mod.displayLabel}</h1>
        {mod.estimatedMinutes > 0 && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <Clock className="w-3 h-3" /> ~{mod.estimatedMinutes} min
          </p>
        )}
      </div>

      {mod.missingContent.length > 0 && (
        <div className="px-4 py-2"><MissingBanner items={mod.missingContent} /></div>
      )}

      {mod.learningOutcome && currentSection === 0 && (
        <div className="px-4 py-2">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
            <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2">Learning Objectives</p>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{mod.learningOutcome}</p>
          </div>
        </div>
      )}

      {currentSection === 0 && (
        <div className="px-4 py-2">
          {mod.videoUrl ? (
            <video controls className="w-full rounded-xl border border-border" preload="metadata">
              <source src={mod.videoUrl} type="video/mp4" />
            </video>
          ) : (
            <div className="aspect-video bg-muted rounded-xl flex items-center justify-center border border-dashed border-border">
              <div className="text-center px-4">
                <Video className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm font-medium text-muted-foreground">Video production outstanding</p>
              </div>
            </div>
          )}
        </div>
      )}

      {currentSection === 0 && mod.safetyWarnings && (
        <div className="px-4 py-2">
          <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex gap-3">
            <ShieldAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-destructive mb-1">⚠ Safety Warning</p>
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{mod.safetyWarnings}</p>
            </div>
          </div>
        </div>
      )}

      {hasSections ? (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Section {currentSection + 1} of {sections.length}
            </span>
          </div>
          <h2 className="text-base font-semibold text-foreground mb-3">{sections[currentSection]?.title}</h2>
          <SectionContent section={sections[currentSection]} />
        </div>
      ) : (
        <div className="px-4 py-8 text-center">
          <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No learning content sections added yet</p>
        </div>
      )}

      {currentSection === sections.length - 1 && mod.commonMistakes && (
        <div className="px-4 py-2">
          <div className="bg-muted rounded-xl p-4">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">⚠ Common Mistakes to Avoid</p>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{mod.commonMistakes}</p>
          </div>
        </div>
      )}

      {currentSection === sections.length - 1 && practicalItems.length > 0 && (
        <div className="px-4 py-3">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Practical Assessment Criteria</h3>
          <div className="space-y-2">
            {practicalItems.map((item: any, i: number) => (
              <div key={i} className="flex items-start gap-3 p-3 border border-border rounded-lg bg-card">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">{i + 1}</div>
                <div>
                  <p className="text-sm font-medium text-foreground">{item.label || item.criterion}</p>
                  {item.evidenceRule && <p className="text-xs text-muted-foreground mt-0.5">{item.evidenceRule}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-3 flex gap-3 z-50 max-w-2xl mx-auto">
        <Button variant="outline" onClick={() => { if (currentSection > 0) setCurrentSection(s => s - 1); else onClose(); }}
          className="flex-1 h-12 touch-manipulation">
          <ChevronLeft className="w-4 h-4 mr-1" /> {currentSection === 0 ? 'Course' : 'Back'}
        </Button>
        {currentSection < sections.length - 1 ? (
          <Button onClick={() => setCurrentSection(s => s + 1)} className="flex-1 h-12 touch-manipulation">
            Continue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={onClose} className="flex-1 h-12 touch-manipulation">
            Back to Course <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CoursePreview() {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const isV2 = searchParams.get('v2') === 'true';
  const { user, isLoading, loginWithRedirect } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewingModule, setPreviewingModule] = useState<Module | null>(null);
  const [v2Data, setV2Data] = useState<GetCourseBuilderModulesOutputType | null>(null);

  useEffect(() => {
    if (!isLoading && !user) loginWithRedirect({ redirectUrl: window.location.href });
  }, [isLoading, user, loginWithRedirect]);

  useEffect(() => {
    if (!user || !courseId) return;
    if (isV2) {
      getCourseBuilderModules({ courseId })
        .then(d => { setV2Data(d); })
        .catch(e => setError(e?.message || 'Could not load V2 data'))
        .finally(() => setLoading(false));
    }
    getAdminCoursePreview({ courseId })
      .then(setData)
      .catch(e => { if (!isV2) setError(e?.message || 'Could not load course'); })
      .finally(() => { if (!isV2) setLoading(false); });
  }, [user, courseId, isV2]);

  if (isLoading || !user) return null;

  // V2 preview — full interactive player
  if (isV2 && v2Data) {
    return <V2PreviewPlayer modules={v2Data.modules} course={v2Data.course} onBack={() => navigate(`/train/admin/builder/${courseId}`)} />;
  }

  if (previewingModule) {
    return <ModulePreview mod={previewingModule} onClose={() => setPreviewingModule(null)} />;
  }

  if (loading) return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
      <Skeleton className="h-6 w-40" /><Skeleton className="h-24" /><Skeleton className="h-4 w-full" />
      {[1,2,3,4].map(i => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  if (error || !data) return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-center">
      <ShieldAlert className="w-12 h-12 text-destructive mx-auto mb-3" />
      <h2 className="font-semibold text-foreground mb-1">Cannot Preview Course</h2>
      <p className="text-sm text-muted-foreground mb-4">{error || 'Course not found or access denied.'}</p>
      <Link to="/train/admin" className="text-sm text-primary hover:underline">← Back to Training Admin</Link>
    </div>
  );

  const { course, modules } = data;

  return (
    <div className="max-w-2xl mx-auto pb-8">
      <div className="bg-muted border-b border-border px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground">PREVIEW AS LEARNER</span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
          course.status === 'Draft' ? 'bg-muted-foreground/20 text-muted-foreground' : 'bg-primary/10 text-primary'
        }`}>{course.status} — Test Only</span>
      </div>

      <div className="px-4 py-4 border-b border-border bg-card">
        <Link to="/train/admin?section=courses" className="text-xs text-muted-foreground flex items-center gap-1 mb-3 hover:text-foreground">
          <ArrowLeft className="w-3.5 h-3.5" /> Training Administration
        </Link>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{course.courseCode}</span>
          <span className="text-xs text-muted-foreground">v{course.currentVersion}</span>
        </div>
        <h1 className="text-xl font-bold text-foreground mb-1">{course.title}</h1>
        {course.description && <p className="text-sm text-muted-foreground leading-relaxed mb-3">{course.description}</p>}

        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>{modules.length} modules</span>
          {data.totalMinutes > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> ~{data.totalMinutes} min total</span>}
          <span>Pass mark: {course.defaultPassMark}%</span>
          <span>{course.sequentialModules ? 'Sequential' : 'Any order'}</span>
          <span>Review: {course.reviewPeriod}</span>
        </div>

        {data.missingContentCount > 0 && (
          <div className="mt-3 bg-muted border border-border rounded-lg p-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground">
              {data.missingContentCount} missing content item{data.missingContentCount !== 1 ? 's' : ''} across modules
            </span>
          </div>
        )}
      </div>

      <div className="px-4 pt-4">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" /> Modules ({modules.length})
        </h2>
        <div className="space-y-2">
          {modules.map((m) => (
            <button key={m.id} onClick={() => setPreviewingModule(m)}
              className="w-full text-left flex items-center gap-3 p-4 rounded-xl border-2 border-border hover:border-primary/30 active:bg-muted/50 transition-all touch-manipulation cursor-pointer">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-primary/10 text-primary">
                <span className="text-sm font-bold">{m.displayOrder}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{m.displayLabel}</p>
                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                  {m.estimatedMinutes > 0 && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{m.estimatedMinutes} min</span>
                  )}
                  <span className="text-xs text-muted-foreground">{m.sectionCount} section{m.sectionCount !== 1 ? 's' : ''}</span>
                  {m.hasPracticalAssessment && (
                    <span className="text-xs text-primary flex items-center gap-1"><CheckCircle className="w-3 h-3" />Practical</span>
                  )}
                  {m.missingContent.length > 0 && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />{m.missingContent.length} missing
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-6">
        <div className="bg-muted/50 rounded-xl p-4 space-y-2">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Course Settings</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <span>Invigilator Roles: {course.invigilatorRoles.join(', ') || 'Not set'}</span>
            <span>Retention: {course.retentionPeriod || 'Not set'}</span>
            <span>Critical Qs: {course.criticalQuestionRule ? 'Must all be correct' : 'Standard'}</span>
            <span>Sequential: {course.sequentialModules ? 'Yes' : 'No'}</span>
          </div>
          {course.employeeDeclarationText && (
            <div className="pt-2 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground mb-1">Employee Declaration</p>
              <p className="text-xs text-muted-foreground">{course.employeeDeclarationText}</p>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pt-4 pb-6">
        <div className="border-2 border-dashed border-border rounded-xl p-4 flex items-center gap-3">
          <Award className="w-9 h-9 text-muted-foreground" />
          <div>
            <p className="text-sm font-semibold text-foreground">Certificate</p>
            <p className="text-xs text-muted-foreground">Employees earn a certificate after completing all modules, quizzes, practical assessments and signing declarations</p>
          </div>
        </div>
      </div>
    </div>
  );
}
